import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
// Packaging note: Colors should be handled by using ThemeProvider in the @benchling/nucleus-ui package.
import {BLUE_POWDER_LIGHT, GRAY_DARK, GRAY_DARKER} from 'ui-library/Colors';

const ICON_WIDTH = 16;
const SPACER_WIDTH = 4;

interface RowProps {
  checkbox: ReactNode;
  description?: string;

  expandIcon: ReactNode;

  /**
   * If `null`, no type icon will be rendered and the space will not be used.
   */
  typeIcon: ReactNode;
  indentationLevel: number;
  text: ReactNode;
  onClick: () => void;
}

/**
 * Render a row in the `GraphQLFieldSelector` component.
 *
 * `Render` `|Checkbox|...1|Expand Icon|Type Icon|Text|...2|`, where:
 * - `|...1|` is the dynamic spacing based on `indentationLevel` prop
 * - `|...2|` is the remaining space in the row
 *
 * @package
 */
export default function Row({
  checkbox,
  description,
  expandIcon,
  typeIcon,
  indentationLevel,
  text,
  onClick,
}: RowProps): JSX.Element {
  return (
    <div
      className={css(rowStyles.wrapper)}
      onClick={onClick}
      data-indentationlevel={indentationLevel}
      {...getTestProps({component: 'Row', element: 'root'})}
    >
      <div
        className={css(rowStyles.checkboxWrapper)}
        {...getTestProps({component: 'Row', element: 'checkboxWrapper'})}
      >
        {checkbox}
      </div>
      <div
        className={css(rowStyles.spacing)}
        style={{
          // Move over by the width of the expand icon + the spacing, so that this row's expand icon aligns
          // with the previous indentation level's type icons.
          width: indentationLevel * (ICON_WIDTH + SPACER_WIDTH),
        }}
        {...getTestProps({component: 'Row', element: 'spacing'})}
      />
      <div
        className={css(rowStyles.expandIconWrapper)}
        {...getTestProps({component: 'Row', element: 'expandIconWrapper'})}
      >
        {expandIcon}
      </div>
      {typeIcon && (
        <div
          className={css(rowStyles.typeIconWrapper)}
          {...getTestProps({component: 'Row', element: 'typeIconWrapper'})}
        >
          {typeIcon}
        </div>
      )}
      <div
        className={css(rowStyles.textWrapper)}
        {...getTestProps({component: 'Row', element: 'textWrapper'})}
      >
        {text}
      </div>
      {description && (
        <div
          className={css(rowStyles.descriptionWrapper)}
          {...getTestProps({component: 'Row', element: 'descriptionWrapper'})}
        >
          {description}
        </div>
      )}
    </div>
  );
}

const SHARED_ICON_STYLES: CSSProperties = {
  width: ICON_WIDTH,
  height: ICON_WIDTH,
  display: 'flex', // Needed to vertically center icons
  flexShrink: 0,
  fontSize: 16,
  overflow: 'hidden',
};

/** @package */
export const rowStyles = StyleSheet.create({
  wrapper: {
    height: 25,
    padding: 4,
    overflow: 'hidden',
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    userSelect: 'none',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  checkboxWrapper: {
    width: ICON_WIDTH,
    height: ICON_WIDTH,
    flexShrink: 0,
    overflow: 'hidden',
  },
  expandIconWrapper: {
    ...SHARED_ICON_STYLES,
    marginRight: SPACER_WIDTH,
    color: GRAY_DARKER,
  },
  typeIconWrapper: {
    ...SHARED_ICON_STYLES,
    color: GRAY_DARK,
    marginRight: 4,
  },
  descriptionWrapper: {
    color: GRAY_DARK,
    flexGrow: 1,
    marginLeft: SPACER_WIDTH,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  textWrapper: {
    overflow: 'hidden',
    flexShrink: 0,
  },
  spacing: {
    flexShrink: 0,
  },
});
